import { HelpfulOption } from "#/components/carrot-plans/most-helpful-options/helpfulOption"
import { defineMessages } from "react-intl"
import { JourneyStage } from "#/components/carrot-plans/types/journeyStage"
import { Journey } from "#/components/carrot-plans/types/journey"
import { PreservationInterest } from "#/components/carrot-plans/types/preservationInterest"

export enum CategoryTitle {
  INITIAL_CATEGORY = "INITIAL_CATEGORY",
  PRENATAL_HEALTH_CATEGORY = "PRENATAL_HEALTH_CATEGORY",
  FETAL_DEVELOPMENT_CATEGORY = "FETAL_DEVELOPMENT_CATEGORY",
  EARLY_BIRTH_PLANNING_CATEGORY = "EARLY_BIRTH_PLANNING_CATEGORY",
  HIGH_RISK_PREGNANCY_CATEGORY = "HIGH_RISK_PREGNANCY_CATEGORY",
  BIRTH_PREPARATION_CATEGORY = "BIRTH_PREPARATION_CATEGORY",
  RETURN_TO_WORK_CATEGORY = "RETURN_TO_WORK_CATEGORY",
  BREASTFEEDING_CATEGORY = "BREASTFEEDING_CATEGORY",
  POSTPARTUM_EMOTIONS_CATEGORY = "POSTPARTUM_EMOTIONS_CATEGORY",
  POSTPARTUM_PHYSICAL_CATEGORY = "POSTPARTUM_PHYSICAL_CATEGORY",
  FERTILITY_BASICS_CATEGORY = "FERTILITY_BASICS_CATEGORY",
  COMMON_FERTILITY_CHALLENGES_CATEGORY = "COMMON_FERTILITY_CHALLENGES_CATEGORY",
  PRE_PREGNANCY_HEALTH_CATEGORY = "PRE_PREGNANCY_HEALTH_CATEGORY",
  TESTING_AND_CONSULTATIONS_CATEGORY = "TESTING_AND_CONSULTATIONS_CATEGORY",
  ASSISTED_REPRODUCTION_BASICS_CATEGORY = "ASSISTED_REPRODUCTION_BASICS_CATEGORY",
  DONOR_ASSISTANCE_CATEGORY = "DONOR_ASSISTANCE_CATEGORY",
  PREPARING_FOR_IUI_CATEGORY = "PREPARING_FOR_IUI_CATEGORY",
  PREPARING_FOR_IVF_CATEGORY = "PREPARING_FOR_IVF_CATEGORY",
  UNDERSTANDING_IVF_CATEGORY = "UNDERSTANDING_IVF_CATEGORY",
  INFANT_HEALTH_NUTRITION_AND_SAFETY_CATEGORY = "INFANT_HEALTH_NUTRITION_AND_SAFETY_CATEGORY",
  INFANT_LIFESTYLE_CATEGORY = "INFANT_LIFESTYLE_CATEGORY",
  PARENT_SELF_CARE_CATEGORY = "PARENT_SELF_CARE_CATEGORY",
  CHILD_HEALTH_CATEGORY = "CHILD_HEALTH_CATEGORY",
  CHILD_LIFESTYLE_CATEGORY = "CHILD_LIFESTYLE_CATEGORY",
  FETAL_DEVELOPMENT_PRENATAL_CARE_CATEGORY = "FETAL_DEVELOPMENT_PRENATAL_CARE_CATEGORY",
  BIRTH_PLANNING_PREP_INTENDED_PARENTS_CATEGORY = "BIRTH_PLANNING_PREP_INTENDED_PARENTS_CATEGORY",
  POST_BIRTH_EMOTIONS_MENTAL_HEALTH_CATEGORY = "POST_BIRTH_EMOTIONS_MENTAL_HEALTH_CATEGORY",
  INFANT_FEEDING_CATEGORY = "INFANT_FEEDING_CATEGORY"
}

export interface CategoryOption {
  title?: CategoryTitle
  options: HelpfulOption[]
}

export interface FlattenedOption {
  label: string | null
  value: string
  isCategory: boolean
  isInitial: boolean
}

export const categoryTitleLabels = Object.entries(
  defineMessages({
    [CategoryTitle.PRENATAL_HEALTH_CATEGORY]: { defaultMessage: "Prenatal health and wellness" },
    [CategoryTitle.FETAL_DEVELOPMENT_CATEGORY]: { defaultMessage: "Fetal development" },
    [CategoryTitle.EARLY_BIRTH_PLANNING_CATEGORY]: { defaultMessage: "Early birth planning considerations" },
    [CategoryTitle.HIGH_RISK_PREGNANCY_CATEGORY]: { defaultMessage: "High-risk pregnancy" },
    [CategoryTitle.BIRTH_PREPARATION_CATEGORY]: { defaultMessage: "Birth preparation" },
    [CategoryTitle.RETURN_TO_WORK_CATEGORY]: { defaultMessage: "Return to work" },
    [CategoryTitle.BREASTFEEDING_CATEGORY]: { defaultMessage: "Lactation" },
    [CategoryTitle.POSTPARTUM_EMOTIONS_CATEGORY]: { defaultMessage: "Postpartum emotions and mental health" },
    [CategoryTitle.POSTPARTUM_PHYSICAL_CATEGORY]: { defaultMessage: "Postpartum physical health" },
    [CategoryTitle.FERTILITY_BASICS_CATEGORY]: { defaultMessage: "Fertility basics" },
    [CategoryTitle.COMMON_FERTILITY_CHALLENGES_CATEGORY]: { defaultMessage: "Common fertility challenges" },
    [CategoryTitle.PRE_PREGNANCY_HEALTH_CATEGORY]: { defaultMessage: "Pre-pregnancy health" },
    [CategoryTitle.TESTING_AND_CONSULTATIONS_CATEGORY]: { defaultMessage: "Testing and consultations" },
    [CategoryTitle.ASSISTED_REPRODUCTION_BASICS_CATEGORY]: { defaultMessage: "Assisted reproduction basics" },
    [CategoryTitle.DONOR_ASSISTANCE_CATEGORY]: { defaultMessage: "Donor assistance" },
    [CategoryTitle.PREPARING_FOR_IUI_CATEGORY]: { defaultMessage: "Preparing for IUI" },
    [CategoryTitle.PREPARING_FOR_IVF_CATEGORY]: { defaultMessage: "Preparing for IVF" },
    [CategoryTitle.UNDERSTANDING_IVF_CATEGORY]: { defaultMessage: "Understanding IVF" },
    [CategoryTitle.INFANT_HEALTH_NUTRITION_AND_SAFETY_CATEGORY]: {
      defaultMessage: "Infant health, nutrition, and safety"
    },
    [CategoryTitle.INFANT_LIFESTYLE_CATEGORY]: { defaultMessage: "Infant lifestyle" },
    [CategoryTitle.PARENT_SELF_CARE_CATEGORY]: { defaultMessage: "Parent self care" },
    [CategoryTitle.CHILD_HEALTH_CATEGORY]: { defaultMessage: "Child health" },
    [CategoryTitle.CHILD_LIFESTYLE_CATEGORY]: { defaultMessage: "Child lifestyle" },
    [CategoryTitle.FETAL_DEVELOPMENT_PRENATAL_CARE_CATEGORY]: {
      defaultMessage: "Fetal development and prenatal care"
    },
    [CategoryTitle.BIRTH_PLANNING_PREP_INTENDED_PARENTS_CATEGORY]: {
      defaultMessage: "Birth planning and preparation for intended parent(s)"
    },
    [CategoryTitle.POST_BIRTH_EMOTIONS_MENTAL_HEALTH_CATEGORY]: {
      defaultMessage: "Post-birth emotions and mental health"
    },
    [CategoryTitle.INFANT_FEEDING_CATEGORY]: { defaultMessage: "Infant feeding" }
  })
).map(([value, label]) => ({ label, value }))

// Trying to get pregnant
const haveNotStartedYetOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.FERTILITY_BASICS,
      HelpfulOption.COMMON_FERTILITY_CHALLENGES,
      HelpfulOption.PRE_PREGNANCY_HEALTH,
      HelpfulOption.IMPROVING_METABOLIC_HEALTH,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.RELATIONSHIP_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.FERTILITY_BASICS_CATEGORY,
    options: [
      HelpfulOption.OVULATION_TRACKING,
      HelpfulOption.HORMONAL_CONTRACEPTION_AND_FERTILITY,
      HelpfulOption.LGBTQIA_PLUS_SUPPORT,
      HelpfulOption.TRANSGENDER_FERTILITY_CONSIDERATIONS,
      HelpfulOption.SPERM_HEALTH,
      HelpfulOption.VASECTOMY_REVERSAL
    ]
  },
  {
    title: CategoryTitle.COMMON_FERTILITY_CHALLENGES_CATEGORY,
    options: [
      HelpfulOption.POLYCYSTIC_OVARY_SYNDROME_PCOS,
      HelpfulOption.ENDOMETRIOSIS,
      HelpfulOption.LOW_OR_HIGH_BMI,
      HelpfulOption.UTERINE_DISORDERS,
      HelpfulOption.DIMINISHED_OVARIAN_RESERVE,
      HelpfulOption.TUBAL_FACTOR,
      HelpfulOption.RECURRENT_PREGNANCY_LOSS,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.EARLY_MENOPAUSE_OR_PRIMARY_OVARIAN_INSUFFICIENCY,
      HelpfulOption.UNEXPLAINED_INFERTILITY,
      HelpfulOption.ERECTILE_DYSFUNCTION
    ]
  },
  {
    title: CategoryTitle.PRE_PREGNANCY_HEALTH_CATEGORY,
    options: [
      HelpfulOption.NUTRITION,
      HelpfulOption.SUPPLEMENTS_FOR_PRE_PREGNANCY,
      HelpfulOption.OPTIMIZING_SPERM_HEALTH,
      HelpfulOption.GLP1_TRYING_TO_GET_PREGNANT,
      HelpfulOption.PRE_EXISTING_MEDICAL_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  }
]

const tryingLessThanSixMonths: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.FERTILITY_BASICS,
      HelpfulOption.COMMON_FERTILITY_CHALLENGES,
      HelpfulOption.PRE_PREGNANCY_HEALTH,
      HelpfulOption.IMPROVING_METABOLIC_HEALTH,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.RELATIONSHIP_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.FERTILITY_BASICS_CATEGORY,
    options: [
      HelpfulOption.OVULATION_TRACKING,
      HelpfulOption.HORMONAL_CONTRACEPTION_AND_FERTILITY,
      HelpfulOption.LGBTQIA_PLUS_SUPPORT,
      HelpfulOption.TRANSGENDER_FERTILITY_CONSIDERATIONS,
      HelpfulOption.SPERM_HEALTH,
      HelpfulOption.VASECTOMY_REVERSAL
    ]
  },
  {
    title: CategoryTitle.COMMON_FERTILITY_CHALLENGES_CATEGORY,
    options: [
      HelpfulOption.POLYCYSTIC_OVARY_SYNDROME_PCOS,
      HelpfulOption.ENDOMETRIOSIS,
      HelpfulOption.LOW_OR_HIGH_BMI,
      HelpfulOption.UTERINE_DISORDERS,
      HelpfulOption.DIMINISHED_OVARIAN_RESERVE,
      HelpfulOption.TUBAL_FACTOR,
      HelpfulOption.RECURRENT_PREGNANCY_LOSS,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.EARLY_MENOPAUSE_OR_PRIMARY_OVARIAN_INSUFFICIENCY,
      HelpfulOption.UNEXPLAINED_INFERTILITY,
      HelpfulOption.ERECTILE_DYSFUNCTION
    ]
  },
  {
    title: CategoryTitle.PRE_PREGNANCY_HEALTH_CATEGORY,
    options: [
      HelpfulOption.NUTRITION,
      HelpfulOption.SUPPLEMENTS_FOR_PRE_PREGNANCY,
      HelpfulOption.OPTIMIZING_SPERM_HEALTH,
      HelpfulOption.GLP1_TRYING_TO_GET_PREGNANT,
      HelpfulOption.PRE_EXISTING_MEDICAL_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  }
]

const tryingSixToTwelveMonths: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.FERTILITY_BASICS,
      HelpfulOption.COMMON_FERTILITY_CHALLENGES,
      HelpfulOption.PRE_PREGNANCY_HEALTH,
      HelpfulOption.IMPROVING_METABOLIC_HEALTH,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.RELATIONSHIP_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.FERTILITY_BASICS_CATEGORY,
    options: [
      HelpfulOption.OVULATION_TRACKING,
      HelpfulOption.HORMONAL_CONTRACEPTION_AND_FERTILITY,
      HelpfulOption.LGBTQIA_PLUS_SUPPORT,
      HelpfulOption.TRANSGENDER_FERTILITY_CONSIDERATIONS,
      HelpfulOption.SPERM_HEALTH,
      HelpfulOption.VASECTOMY_REVERSAL
    ]
  },
  {
    title: CategoryTitle.COMMON_FERTILITY_CHALLENGES_CATEGORY,
    options: [
      HelpfulOption.POLYCYSTIC_OVARY_SYNDROME_PCOS,
      HelpfulOption.ENDOMETRIOSIS,
      HelpfulOption.LOW_OR_HIGH_BMI,
      HelpfulOption.UTERINE_DISORDERS,
      HelpfulOption.DIMINISHED_OVARIAN_RESERVE,
      HelpfulOption.TUBAL_FACTOR,
      HelpfulOption.RECURRENT_PREGNANCY_LOSS,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.EARLY_MENOPAUSE_OR_PRIMARY_OVARIAN_INSUFFICIENCY,
      HelpfulOption.UNEXPLAINED_INFERTILITY,
      HelpfulOption.ERECTILE_DYSFUNCTION
    ]
  },
  {
    title: CategoryTitle.PRE_PREGNANCY_HEALTH_CATEGORY,
    options: [
      HelpfulOption.NUTRITION,
      HelpfulOption.SUPPLEMENTS_FOR_PRE_PREGNANCY,
      HelpfulOption.OPTIMIZING_SPERM_HEALTH,
      HelpfulOption.GLP1_TRYING_TO_GET_PREGNANT,
      HelpfulOption.PRE_EXISTING_MEDICAL_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  }
]

const tryingOverOneYear: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.IMPROVING_FERTILITY_WITHOUT_MEDICAL_INTERVENTIONS,
      HelpfulOption.IMPROVING_METABOLIC_HEALTH,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.COMMON_FERTILITY_CHALLENGES,
      HelpfulOption.PRE_PREGNANCY_HEALTH,
      HelpfulOption.TESTING_AND_CONSULTATIONS,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.RELATIONSHIP_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.COMMON_FERTILITY_CHALLENGES_CATEGORY,
    options: [
      HelpfulOption.POLYCYSTIC_OVARY_SYNDROME_PCOS,
      HelpfulOption.ENDOMETRIOSIS,
      HelpfulOption.LOW_OR_HIGH_BMI,
      HelpfulOption.UTERINE_DISORDERS,
      HelpfulOption.DIMINISHED_OVARIAN_RESERVE,
      HelpfulOption.TUBAL_FACTOR,
      HelpfulOption.RECURRENT_PREGNANCY_LOSS,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.EARLY_MENOPAUSE_OR_PRIMARY_OVARIAN_INSUFFICIENCY,
      HelpfulOption.UNEXPLAINED_INFERTILITY,
      HelpfulOption.ERECTILE_DYSFUNCTION
    ]
  },
  {
    title: CategoryTitle.PRE_PREGNANCY_HEALTH_CATEGORY,
    options: [
      HelpfulOption.NUTRITION,
      HelpfulOption.SUPPLEMENTS_FOR_PRE_PREGNANCY,
      HelpfulOption.OPTIMIZING_SPERM_HEALTH,
      HelpfulOption.GLP1_TRYING_TO_GET_PREGNANT,
      HelpfulOption.PRE_EXISTING_MEDICAL_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  },
  {
    title: CategoryTitle.TESTING_AND_CONSULTATIONS_CATEGORY,
    options: [
      HelpfulOption.HELP_PREPARING_FOR_AN_APPOINTMENT,
      HelpfulOption.INITIAL_CONSULTATIONS,
      HelpfulOption.FERTILITY_EVALUATION,
      HelpfulOption.OVARIAN_RESERVE_TESTING,
      HelpfulOption.SEMEN_ANALYSIS,
      HelpfulOption.TRANSVAGINAL_ULTRASOUND,
      HelpfulOption.UTERINE_SCREENING,
      HelpfulOption.GENETIC_CARRIER_SCREENING
    ]
  }
]

// Pregnant
const pregnantFirstTrimesterOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PRENATAL_HEALTH_WELLNESS,
      HelpfulOption.FETAL_DEVELOPMENT,
      HelpfulOption.HELP_FINDING_OBSTETRIC_PROVIDER,
      HelpfulOption.HELP_FINDING_DOULA,
      HelpfulOption.DOULA_GENERAL_INFO,
      HelpfulOption.HIGH_RISK_PREGNANCY,
      HelpfulOption.EARLY_BIRTH_PLANNING,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.EARLY_BIRTH_PLANNING_CATEGORY,
    options: [
      HelpfulOption.BIRTH_OPTIONS_SETTINGS,
      HelpfulOption.BIRTH_PLAN_RESOURCES,
      HelpfulOption.PREGNANCY_SUPPORT_TEAM,
      HelpfulOption.PHYSICAL_PREPARATION,
      HelpfulOption.EMOTIONAL_PREPARATION,
      HelpfulOption.PARENTAL_LEAVE_PLAN
    ]
  },
  {
    title: CategoryTitle.HIGH_RISK_PREGNANCY_CATEGORY,
    options: [
      HelpfulOption.ADVANCED_MATERNAL_AGE,
      HelpfulOption.DIABETES_MANAGEMENT,
      HelpfulOption.BLOOD_PRESSURE_MANAGEMENT,
      HelpfulOption.CHRONIC_CONDITIONS,
      HelpfulOption.MULTIPLE_FETUSES,
      HelpfulOption.UTERINE_CONDITIONS,
      HelpfulOption.HIGH_RISK_PREGNANCY_HISTORY,
      HelpfulOption.PRIOR_COMPLICATED_BIRTH
    ]
  },
  {
    title: CategoryTitle.FETAL_DEVELOPMENT_CATEGORY,
    options: [
      HelpfulOption.FIRST_TRIMESTER_DEVELOPMENT,
      HelpfulOption.GENETIC_SCREENING_PRENATAL_TESTING,
      HelpfulOption.ULTRASOUND_IMAGING,
      HelpfulOption.PREGNANCY_RISKS_COMPLICATIONS,
      HelpfulOption.PREGNANCY_WARNING_SIGNS
    ]
  },
  {
    title: CategoryTitle.PRENATAL_HEALTH_CATEGORY,
    options: [
      HelpfulOption.PREGNANCY_EXERCISE,
      HelpfulOption.PREGNANCY_DISCOMFORTS,
      HelpfulOption.STRESS_MANAGEMENT,
      HelpfulOption.SLEEP_FATIGUE,
      HelpfulOption.MEDICATION_SUPPLEMENTS,
      HelpfulOption.PRENATAL_YOGA
    ]
  }
]

const pregnantSecondTrimesterOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PRENATAL_HEALTH_WELLNESS,
      HelpfulOption.FETAL_DEVELOPMENT,
      HelpfulOption.HELP_FINDING_OBSTETRIC_PROVIDER,
      HelpfulOption.HELP_FINDING_DOULA,
      HelpfulOption.DOULA_GENERAL_INFO,
      HelpfulOption.HIGH_RISK_PREGNANCY,
      HelpfulOption.EARLY_BIRTH_PLANNING,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.EARLY_BIRTH_PLANNING_CATEGORY,
    options: [
      HelpfulOption.BIRTH_OPTIONS_SETTINGS,
      HelpfulOption.BIRTH_PLAN_RESOURCES,
      HelpfulOption.PREGNANCY_SUPPORT_TEAM,
      HelpfulOption.PHYSICAL_PREPARATION,
      HelpfulOption.EMOTIONAL_PREPARATION,
      HelpfulOption.PARENTAL_LEAVE_PLAN,
      HelpfulOption.PRENATAL_EDUCATION
    ]
  },
  {
    title: CategoryTitle.HIGH_RISK_PREGNANCY_CATEGORY,
    options: [
      HelpfulOption.ADVANCED_MATERNAL_AGE,
      HelpfulOption.BLOOD_PRESSURE_MANAGEMENT,
      HelpfulOption.CHRONIC_CONDITIONS,
      HelpfulOption.MULTIPLE_FETUSES,
      HelpfulOption.UTERINE_CONDITIONS,
      HelpfulOption.HIGH_RISK_PREGNANCY_HISTORY,
      HelpfulOption.PRIOR_COMPLICATED_BIRTH,
      HelpfulOption.DIABETES_MANAGEMENT
    ]
  },
  {
    title: CategoryTitle.FETAL_DEVELOPMENT_CATEGORY,
    options: [
      HelpfulOption.ULTRASOUND_IMAGING,
      HelpfulOption.PREGNANCY_RISKS_COMPLICATIONS,
      HelpfulOption.PREGNANCY_WARNING_SIGNS,
      HelpfulOption.SECOND_TRIMESTER_DEVELOPMENT,
      HelpfulOption.GENETIC_SCREENING_PRENATAL_TESTING,
      HelpfulOption.FETAL_MOVEMENT
    ]
  },
  {
    title: CategoryTitle.PRENATAL_HEALTH_CATEGORY,
    options: [
      HelpfulOption.PREGNANCY_EXERCISE,
      HelpfulOption.PREGNANCY_DISCOMFORTS,
      HelpfulOption.STRESS_MANAGEMENT,
      HelpfulOption.SLEEP_FATIGUE,
      HelpfulOption.MEDICATION_SUPPLEMENTS,
      HelpfulOption.PRENATAL_YOGA
    ]
  }
]

const pregnantThirdTrimesterOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PRENATAL_HEALTH_WELLNESS,
      HelpfulOption.FETAL_DEVELOPMENT,
      HelpfulOption.HELP_FINDING_PEDIATRICIAN,
      HelpfulOption.HELP_FINDING_DOULA,
      HelpfulOption.DOULA_GENERAL_INFO,
      HelpfulOption.BIRTH_PREPARATION,
      HelpfulOption.HIGH_RISK_PREGNANCY,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.BIRTH_PREPARATION_CATEGORY,
    options: [
      HelpfulOption.HELP_FINDING_OBSTETRIC_PROVIDER,
      HelpfulOption.VAGINAL_BIRTH_AFTER_CESAREAN,
      HelpfulOption.SIGNS_OF_LABOR,
      HelpfulOption.STAGES_OF_LABOR,
      HelpfulOption.PAIN_MANAGEMENT,
      HelpfulOption.POSTPARTUM_PLAN,
      HelpfulOption.PARENTAL_LEAVE_RETURN_TO_WORK_PLAN,
      HelpfulOption.SUPPORT_LACTATION_INFANT_FEEDING,
      HelpfulOption.PREGNANCY_SUPPORT_TEAM,
      HelpfulOption.PRENATAL_EDUCATION,
      HelpfulOption.PHYSICAL_PREPARATION,
      HelpfulOption.EMOTIONAL_PREPARATION
    ]
  },
  {
    title: CategoryTitle.HIGH_RISK_PREGNANCY_CATEGORY,
    options: [
      HelpfulOption.ADVANCED_MATERNAL_AGE,
      HelpfulOption.CHRONIC_CONDITIONS,
      HelpfulOption.MULTIPLE_FETUSES,
      HelpfulOption.UTERINE_CONDITIONS,
      HelpfulOption.HIGH_RISK_PREGNANCY_HISTORY,
      HelpfulOption.PRIOR_COMPLICATED_BIRTH,
      HelpfulOption.DIABETES_MANAGEMENT,
      HelpfulOption.HIGH_BLOOD_PRESSURE_PREECLAMPSIA
    ]
  },
  {
    title: CategoryTitle.FETAL_DEVELOPMENT_CATEGORY,
    options: [
      HelpfulOption.ULTRASOUND_IMAGING,
      HelpfulOption.PREGNANCY_RISKS_COMPLICATIONS,
      HelpfulOption.PREGNANCY_WARNING_SIGNS,
      HelpfulOption.GENETIC_SCREENING_PRENATAL_TESTING,
      HelpfulOption.FETAL_MOVEMENT,
      HelpfulOption.THIRD_TRIMESTER_DEVELOPMENT,
      HelpfulOption.BREECH_POSITION
    ]
  },
  {
    title: CategoryTitle.PRENATAL_HEALTH_CATEGORY,
    options: [
      HelpfulOption.PREGNANCY_EXERCISE,
      HelpfulOption.PREGNANCY_DISCOMFORTS,
      HelpfulOption.STRESS_MANAGEMENT,
      HelpfulOption.SLEEP_FATIGUE,
      HelpfulOption.MEDICATION_SUPPLEMENTS,
      HelpfulOption.PLANNED_CESAREAN,
      HelpfulOption.PRENATAL_YOGA
    ]
  }
]

const pregnantPostpartumOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.POSTPARTUM_PHYSICAL_HEALTH,
      HelpfulOption.POSTPARTUM_MENTAL_HEALTH,
      HelpfulOption.HELP_FINDING_PEDIATRICIAN,
      HelpfulOption.BREASTFEEDING_INFO,
      HelpfulOption.RETURN_TO_WORK,
      HelpfulOption.DOULA_POSTPARTUM_INFO,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.RETURN_TO_WORK_CATEGORY,
    options: [
      HelpfulOption.RETURN_TO_WORK_PLAN,
      HelpfulOption.EMOTIONAL_CHALLENGES_WORK,
      HelpfulOption.MANAGER_CONVERSATIONS,
      HelpfulOption.WORK_LIFE_BALANCE,
      HelpfulOption.SLEEP_DEPRIVATION_WORK,
      HelpfulOption.PUMPING_AT_WORK,
      HelpfulOption.REMOTE_WORK_NEWBORN
    ]
  },
  {
    title: CategoryTitle.BREASTFEEDING_CATEGORY,
    options: [
      HelpfulOption.LATCH_POSITION,
      HelpfulOption.MILK_SUPPLY,
      HelpfulOption.NIPPLE_SORENESS,
      HelpfulOption.MILK_PUMPING_STORAGE,
      HelpfulOption.INDUCED_LACTATION,
      HelpfulOption.COMBINATION_FEEDING,
      HelpfulOption.CESAREAN_RECOVERY_FEEDING,
      HelpfulOption.NICU_FEEDING,
      HelpfulOption.RETURN_TO_WORK_FEEDING
    ]
  },
  {
    title: CategoryTitle.POSTPARTUM_EMOTIONS_CATEGORY,
    options: [
      HelpfulOption.NEW_PARENT_EMOTIONS,
      HelpfulOption.PERINATAL_MOOD_DISORDERS,
      HelpfulOption.TRAUMATIC_BIRTH,
      HelpfulOption.BIRTH_IN_NICU,
      HelpfulOption.SELF_CARE,
      HelpfulOption.FINDING_HELP_SUPPORT
    ]
  },
  {
    title: CategoryTitle.POSTPARTUM_PHYSICAL_CATEGORY,
    options: [
      HelpfulOption.POSTPARTUM_WARNING_SIGNS,
      HelpfulOption.VAGINAL_DELIVERY_DISCOMFORT,
      HelpfulOption.CESAREAN_BIRTH_RECOVERY,
      HelpfulOption.POSTPARTUM_CHECKUPS,
      HelpfulOption.PELVIC_HEALTH,
      HelpfulOption.SLEEP_FATIGUE_MANAGEMENT,
      HelpfulOption.FAMILY_PLANNING
    ]
  }
]

// Assisted Reproduction
const exploringArtOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.IMPROVING_FERTILITY_WITHOUT_MEDICAL_INTERVENTIONS,
      HelpfulOption.IMPROVING_METABOLIC_HEALTH,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.ASSISTED_REPRODUCTION_BASICS,
      HelpfulOption.TESTING_AND_CONSULTATIONS,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.TESTING_AND_CONSULTATIONS_CATEGORY,
    options: [
      HelpfulOption.HELP_PREPARING_FOR_AN_APPOINTMENT,
      HelpfulOption.INITIAL_CONSULTATIONS,
      HelpfulOption.FERTILITY_EVALUATION,
      HelpfulOption.OVARIAN_RESERVE_TESTING,
      HelpfulOption.SEMEN_ANALYSIS,
      HelpfulOption.TRANSVAGINAL_ULTRASOUND,
      HelpfulOption.UTERINE_SCREENING,
      HelpfulOption.GENETIC_CARRIER_SCREENING
    ]
  },
  {
    title: CategoryTitle.ASSISTED_REPRODUCTION_BASICS_CATEGORY,
    options: [
      HelpfulOption.UNDERSTANDING_IUI,
      HelpfulOption.UNDERSTANDING_IVF,
      HelpfulOption.FAITH_INCLUSIVE_CARE,
      HelpfulOption.LGBTQIA_PLUS_SUPPORT,
      HelpfulOption.SINGLE_INTENDED_PARENT_SUPPORT,
      HelpfulOption.TRANSGENDER_FERTILITY_CONSIDERATIONS,
      HelpfulOption.MALE_FACTOR_INFERTILITY
    ]
  },
  {
    title: CategoryTitle.DONOR_ASSISTANCE_CATEGORY,
    options: [HelpfulOption.DONOR_EGGS, HelpfulOption.DONOR_SPERM, HelpfulOption.GESTATIONAL_SURROGACY_BASICS]
  }
]

const mightWantArtOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.IMPROVING_FERTILITY_WITHOUT_MEDICAL_INTERVENTIONS,
      HelpfulOption.IMPROVING_METABOLIC_HEALTH,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.ASSISTED_REPRODUCTION_BASICS,
      HelpfulOption.TESTING_AND_CONSULTATIONS,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.TESTING_AND_CONSULTATIONS_CATEGORY,
    options: [
      HelpfulOption.HELP_PREPARING_FOR_AN_APPOINTMENT,
      HelpfulOption.INITIAL_CONSULTATIONS,
      HelpfulOption.FERTILITY_EVALUATION,
      HelpfulOption.OVARIAN_RESERVE_TESTING,
      HelpfulOption.SEMEN_ANALYSIS,
      HelpfulOption.TRANSVAGINAL_ULTRASOUND,
      HelpfulOption.UTERINE_SCREENING,
      HelpfulOption.GENETIC_CARRIER_SCREENING
    ]
  },
  {
    title: CategoryTitle.ASSISTED_REPRODUCTION_BASICS_CATEGORY,
    options: [
      HelpfulOption.UNDERSTANDING_IUI,
      HelpfulOption.UNDERSTANDING_IVF,
      HelpfulOption.FAITH_INCLUSIVE_CARE,
      HelpfulOption.LGBTQIA_PLUS_SUPPORT,
      HelpfulOption.SINGLE_INTENDED_PARENT_SUPPORT,
      HelpfulOption.TRANSGENDER_FERTILITY_CONSIDERATIONS,
      HelpfulOption.MALE_FACTOR_INFERTILITY
    ]
  },
  {
    title: CategoryTitle.DONOR_ASSISTANCE_CATEGORY,
    options: [HelpfulOption.DONOR_EGGS, HelpfulOption.DONOR_SPERM, HelpfulOption.GESTATIONAL_SURROGACY_BASICS]
  }
]

const gettingReadyStartIuiOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PREPARING_FOR_IUI,
      HelpfulOption.ADMINISTERING_MEDICATION,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.PRE_PREGNANCY_HEALTH,
      HelpfulOption.IMPROVING_METABOLIC_HEALTH,
      HelpfulOption.COMMON_FERTILITY_CHALLENGES,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.COMMON_FERTILITY_CHALLENGES_CATEGORY,
    options: [
      HelpfulOption.POLYCYSTIC_OVARY_SYNDROME,
      HelpfulOption.ENDOMETRIOSIS,
      HelpfulOption.LOW_OR_HIGH_BMI,
      HelpfulOption.UTERINE_DISORDERS,
      HelpfulOption.DIMINISHED_OVARIAN_RESERVE,
      HelpfulOption.TUBAL_FACTOR,
      HelpfulOption.RECURRENT_PREGNANCY_LOSS,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.EARLY_MENOPAUSE_OR_PRIMARY_OVARIAN_INSUFFICIENCY,
      HelpfulOption.UNEXPLAINED_INFERTILITY,
      HelpfulOption.ERECTILE_DYSFUNCTION
    ]
  },
  {
    title: CategoryTitle.PRE_PREGNANCY_HEALTH_CATEGORY,
    options: [
      HelpfulOption.NUTRITION,
      HelpfulOption.SUPPLEMENTS_FOR_PRE_PREGNANCY,
      HelpfulOption.OPTIMIZING_SPERM_HEALTH,
      HelpfulOption.PRE_EXISTING_MEDICAL_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  },
  {
    title: CategoryTitle.DONOR_ASSISTANCE_CATEGORY,
    options: [HelpfulOption.DONOR_EGGS, HelpfulOption.DONOR_SPERM, HelpfulOption.GESTATIONAL_SURROGACY_BASICS]
  },
  {
    title: CategoryTitle.PREPARING_FOR_IUI_CATEGORY,
    options: [
      HelpfulOption.UNDERSTANDING_IUI,
      HelpfulOption.GLP1_FERTILITY_TREATMENTS,
      HelpfulOption.FAITH_INCLUSIVE_OPTIONS_FOR_SEMEN_COLLECTION
    ]
  }
]

const gettingReadyStartIvfOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.UNDERSTANDING_IVF,
      HelpfulOption.PREPARING_FOR_IVF,
      HelpfulOption.ADMINISTERING_MEDICATION,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.PRE_PREGNANCY_HEALTH,
      HelpfulOption.IMPROVING_METABOLIC_HEALTH,
      HelpfulOption.COMMON_FERTILITY_CHALLENGES,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.FINANCIAL_CONCERNS_JOURNEY_COST,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.COMMON_FERTILITY_CHALLENGES_CATEGORY,
    options: [
      HelpfulOption.POLYCYSTIC_OVARY_SYNDROME,
      HelpfulOption.ENDOMETRIOSIS,
      HelpfulOption.LOW_OR_HIGH_BMI,
      HelpfulOption.UTERINE_DISORDERS,
      HelpfulOption.DIMINISHED_OVARIAN_RESERVE,
      HelpfulOption.TUBAL_FACTOR,
      HelpfulOption.RECURRENT_PREGNANCY_LOSS,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.EARLY_MENOPAUSE_OR_PRIMARY_OVARIAN_INSUFFICIENCY,
      HelpfulOption.UNEXPLAINED_INFERTILITY,
      HelpfulOption.ERECTILE_DYSFUNCTION
    ]
  },
  {
    title: CategoryTitle.PRE_PREGNANCY_HEALTH_CATEGORY,
    options: [
      HelpfulOption.NUTRITION,
      HelpfulOption.SUPPLEMENTS_FOR_PRE_PREGNANCY,
      HelpfulOption.OPTIMIZING_SPERM_HEALTH,
      HelpfulOption.PRE_EXISTING_MEDICAL_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  },
  {
    title: CategoryTitle.DONOR_ASSISTANCE_CATEGORY,
    options: [HelpfulOption.DONOR_EGGS, HelpfulOption.DONOR_SPERM, HelpfulOption.GESTATIONAL_SURROGACY_BASICS]
  },
  {
    title: CategoryTitle.PREPARING_FOR_IVF_CATEGORY,
    options: [HelpfulOption.GLP1_FERTILITY_TREATMENTS, HelpfulOption.FAITH_INCLUSIVE_OPTIONS_FOR_SEMEN_COLLECTION]
  },
  {
    title: CategoryTitle.UNDERSTANDING_IVF_CATEGORY,
    options: [
      HelpfulOption.EMBRYO_GRADING,
      HelpfulOption.GENETIC_TESTING_OPTIONS,
      HelpfulOption.OVARIAN_STIMULATION,
      HelpfulOption.EGG_RETRIEVAL,
      HelpfulOption.RECIPROCAL_IVF,
      HelpfulOption.FRESH_VS_FROZEN_EMBRYO_TRANSFER,
      HelpfulOption.SINGLE_VS_MULTIPLE_EMBRYO_TRANSFER,
      HelpfulOption.FERTILIZATION_METHODS,
      HelpfulOption.MINIMUM_STIMULATION_AND_NATURAL_CYCLES
    ]
  }
]

const alreadyStartedIuiOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.ADMINISTERING_MEDICATION,
      HelpfulOption.FAITH_INCLUSIVE_OPTIONS_FOR_SEMEN_COLLECTION,
      HelpfulOption.PRE_PREGNANCY_HEALTH,
      HelpfulOption.COMMON_FERTILITY_CHALLENGES,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.COMMON_FERTILITY_CHALLENGES_CATEGORY,
    options: [
      HelpfulOption.POLYCYSTIC_OVARY_SYNDROME_PCOS,
      HelpfulOption.ENDOMETRIOSIS,
      HelpfulOption.LOW_OR_HIGH_BMI,
      HelpfulOption.UTERINE_DISORDERS,
      HelpfulOption.DIMINISHED_OVARIAN_RESERVE,
      HelpfulOption.TUBAL_FACTOR,
      HelpfulOption.RECURRENT_PREGNANCY_LOSS,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.EARLY_MENOPAUSE_OR_PRIMARY_OVARIAN_INSUFFICIENCY,
      HelpfulOption.UNEXPLAINED_INFERTILITY,
      HelpfulOption.ERECTILE_DYSFUNCTION
    ]
  },
  {
    title: CategoryTitle.PRE_PREGNANCY_HEALTH_CATEGORY,
    options: [
      HelpfulOption.NUTRITION,
      HelpfulOption.SUPPLEMENTS_FOR_PRE_PREGNANCY,
      HelpfulOption.OPTIMIZING_SPERM_HEALTH,
      HelpfulOption.PRE_EXISTING_MEDICAL_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  }
]

const alreadyStartedIvfOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.UNDERSTANDING_IVF,
      HelpfulOption.ADMINISTERING_MEDICATION,
      HelpfulOption.FAITH_INCLUSIVE_OPTIONS_FOR_SEMEN_COLLECTION,
      HelpfulOption.PRE_PREGNANCY_HEALTH,
      HelpfulOption.COMMON_FERTILITY_CHALLENGES,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCIAL_CONCERNS_JOURNEY_COST,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.COMMON_FERTILITY_CHALLENGES_CATEGORY,
    options: [
      HelpfulOption.POLYCYSTIC_OVARY_SYNDROME_PCOS,
      HelpfulOption.ENDOMETRIOSIS,
      HelpfulOption.LOW_OR_HIGH_BMI,
      HelpfulOption.UTERINE_DISORDERS,
      HelpfulOption.DIMINISHED_OVARIAN_RESERVE,
      HelpfulOption.TUBAL_FACTOR,
      HelpfulOption.RECURRENT_PREGNANCY_LOSS,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.EARLY_MENOPAUSE_OR_PRIMARY_OVARIAN_INSUFFICIENCY,
      HelpfulOption.UNEXPLAINED_INFERTILITY,
      HelpfulOption.ERECTILE_DYSFUNCTION
    ]
  },
  {
    title: CategoryTitle.PRE_PREGNANCY_HEALTH_CATEGORY,
    options: [
      HelpfulOption.NUTRITION,
      HelpfulOption.SUPPLEMENTS_FOR_PRE_PREGNANCY,
      HelpfulOption.OPTIMIZING_SPERM_HEALTH,
      HelpfulOption.PRE_EXISTING_MEDICAL_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  },
  {
    title: CategoryTitle.UNDERSTANDING_IVF_CATEGORY,
    options: [
      HelpfulOption.EMBRYO_GRADING,
      HelpfulOption.GENETIC_TESTING_OPTIONS,
      HelpfulOption.OVARIAN_STIMULATION,
      HelpfulOption.EGG_RETRIEVAL,
      HelpfulOption.FRESH_VS_FROZEN_EMBRYO_TRANSFER,
      HelpfulOption.SINGLE_VS_MULTIPLE_EMBRYO_TRANSFER,
      HelpfulOption.FERTILIZATION_METHODS,
      HelpfulOption.MINIMUM_STIMULATION_AND_NATURAL_CYCLES
    ]
  }
]

const recentlyCompletedIuiOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.HELP_NAVIGATING_NEXT_STEPS,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.PRE_PREGNANCY_HEALTH,
      HelpfulOption.IMPROVING_METABOLIC_HEALTH,
      HelpfulOption.COMMON_FERTILITY_CHALLENGES,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.COMMON_FERTILITY_CHALLENGES_CATEGORY,
    options: [
      HelpfulOption.POLYCYSTIC_OVARY_SYNDROME_PCOS,
      HelpfulOption.ENDOMETRIOSIS,
      HelpfulOption.LOW_OR_HIGH_BMI,
      HelpfulOption.UTERINE_DISORDERS,
      HelpfulOption.DIMINISHED_OVARIAN_RESERVE,
      HelpfulOption.TUBAL_FACTOR,
      HelpfulOption.RECURRENT_PREGNANCY_LOSS,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.EARLY_MENOPAUSE_OR_PRIMARY_OVARIAN_INSUFFICIENCY,
      HelpfulOption.UNEXPLAINED_INFERTILITY,
      HelpfulOption.ERECTILE_DYSFUNCTION
    ]
  },
  {
    title: CategoryTitle.PRE_PREGNANCY_HEALTH_CATEGORY,
    options: [
      HelpfulOption.NUTRITION,
      HelpfulOption.SUPPLEMENTS_FOR_PRE_PREGNANCY,
      HelpfulOption.OPTIMIZING_SPERM_HEALTH,
      HelpfulOption.PRE_EXISTING_MEDICAL_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  },
  {
    title: CategoryTitle.DONOR_ASSISTANCE_CATEGORY,
    options: [HelpfulOption.DONOR_EGGS, HelpfulOption.DONOR_SPERM, HelpfulOption.GESTATIONAL_SURROGACY_BASICS]
  }
]

const recentlyCompletedIvfOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.HELP_NAVIGATING_NEXT_STEPS,
      HelpfulOption.EMBRYO_STORAGE_AND_CRYOSHIPPING,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.FROZEN_EMBRYO_TRANSFERS,
      HelpfulOption.PRE_PREGNANCY_HEALTH,
      HelpfulOption.IMPROVING_METABOLIC_HEALTH,
      HelpfulOption.COMMON_FERTILITY_CHALLENGES,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.COMMON_FERTILITY_CHALLENGES_CATEGORY,
    options: [
      HelpfulOption.POLYCYSTIC_OVARY_SYNDROME_PCOS,
      HelpfulOption.ENDOMETRIOSIS,
      HelpfulOption.LOW_OR_HIGH_BMI,
      HelpfulOption.UTERINE_DISORDERS,
      HelpfulOption.DIMINISHED_OVARIAN_RESERVE,
      HelpfulOption.TUBAL_FACTOR,
      HelpfulOption.RECURRENT_PREGNANCY_LOSS,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.EARLY_MENOPAUSE_OR_PRIMARY_OVARIAN_INSUFFICIENCY,
      HelpfulOption.UNEXPLAINED_INFERTILITY,
      HelpfulOption.ERECTILE_DYSFUNCTION
    ]
  },
  {
    title: CategoryTitle.PRE_PREGNANCY_HEALTH_CATEGORY,
    options: [
      HelpfulOption.NUTRITION,
      HelpfulOption.SUPPLEMENTS_FOR_PRE_PREGNANCY,
      HelpfulOption.OPTIMIZING_SPERM_HEALTH,
      HelpfulOption.PRE_EXISTING_MEDICAL_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  },
  {
    title: CategoryTitle.DONOR_ASSISTANCE_CATEGORY,
    options: [HelpfulOption.DONOR_EGGS, HelpfulOption.DONOR_SPERM, HelpfulOption.GESTATIONAL_SURROGACY_BASICS]
  }
]

// Parenting
const parentingChildLessThanYearOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PEDIATRIC_TELEMEDICINE,
      HelpfulOption.PARENT_SELF_CARE,
      HelpfulOption.RETURN_TO_WORK,
      HelpfulOption.INFANT_LIFESTYLE,
      HelpfulOption.INFANT_HEALTH_NUTRITION_AND_SAFETY,
      HelpfulOption.SHIPPING_BREAST_CHEST_MILK,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.INFANT_HEALTH_NUTRITION_AND_SAFETY_CATEGORY,
    options: [
      HelpfulOption.INFANT_HEALTH_OR_MEDICAL_CONDITION,
      HelpfulOption.DEVELOPMENTAL_MILESTONES,
      HelpfulOption.SAFETY_CHILDPROOFING,
      HelpfulOption.PREPARING_FORMULA,
      HelpfulOption.BOTTLE_FEEDING,
      HelpfulOption.BABY_LED_WEANING,
      HelpfulOption.INTRODUCING_SOLID_FOOD,
      HelpfulOption.BATHING_DIAPERING_SWADDLING,
      HelpfulOption.SAFE_SLEEP_GUIDELINES
    ]
  },
  {
    title: CategoryTitle.INFANT_LIFESTYLE_CATEGORY,
    options: [
      HelpfulOption.COMFORT_TECHNIQUES_CRYING_FUSSINESS,
      HelpfulOption.INFANT_SLEEP_SLEEP_TRAINING,
      HelpfulOption.INFANT_BEHAVIORS
    ]
  },
  {
    title: CategoryTitle.PARENT_SELF_CARE_CATEGORY,
    options: [
      HelpfulOption.POSTPARTUM_HEALTH_NUTRITION_LIFESTYLE,
      HelpfulOption.NEW_PARENT_EMOTIONS_MENTAL_HEALTH,
      HelpfulOption.PREMATURE_SICK_SPECIAL_NEEDS_INFANT,
      HelpfulOption.BREAST_CHESTFEEDING_SUPPORT
    ]
  },
  {
    title: CategoryTitle.RETURN_TO_WORK_CATEGORY,
    options: [HelpfulOption.BREAST_CHESTFEEDING_AFTER_RETURNING_TO_WORK, HelpfulOption.FINDING_CHILDCARE]
  }
]

const parentingChildOneToTwoYearsOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PEDIATRIC_TELEMEDICINE,
      HelpfulOption.PARENT_SELF_CARE,
      HelpfulOption.RETURN_TO_WORK,
      HelpfulOption.CHILD_LIFESTYLE,
      HelpfulOption.CHILD_HEALTH_NUTRITION_SAFETY,
      HelpfulOption.SHIPPING_BREAST_CHEST_MILK,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.CHILD_HEALTH_CATEGORY,
    options: [
      HelpfulOption.CHILDPROOFING,
      HelpfulOption.SCREEN_TIME_INTERNET_SAFETY,
      HelpfulOption.CHILD_HEALTH_MEDICAL_CONDITION,
      HelpfulOption.DEVELOPMENTAL_MILESTONES,
      HelpfulOption.BABY_LED_WEANING,
      HelpfulOption.CHOOSY_EATERS
    ]
  },
  {
    title: CategoryTitle.CHILD_LIFESTYLE_CATEGORY,
    options: [
      HelpfulOption.CHILD_BEHAVIOR_CHALLENGES,
      HelpfulOption.TRANSITIONING_CRIB_TO_BED,
      HelpfulOption.POTTY_TRAINING,
      HelpfulOption.PREPARING_CHILD_FOR_SIBLING
    ]
  },
  {
    title: CategoryTitle.PARENT_SELF_CARE_CATEGORY,
    options: [
      HelpfulOption.BREAST_CHESTFEEDING_SUPPORT,
      HelpfulOption.PARENT_EMOTIONS_MENTAL_HEALTH,
      HelpfulOption.PARENTING_CHILD_SPECIAL_NEEDS,
      HelpfulOption.RELATIONSHIP_SUPPORT
    ]
  },
  {
    title: CategoryTitle.RETURN_TO_WORK_CATEGORY,
    options: [HelpfulOption.BALANCING_PARENTHOOD_CAREER]
  }
]

const parentingChildThreeToFiveYearsOptions: CategoryOption[] = [...parentingChildOneToTwoYearsOptions]

const parentingChildSixToEightYearsOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PEDIATRIC_TELEMEDICINE,
      HelpfulOption.PARENT_SELF_CARE,
      HelpfulOption.RETURN_TO_WORK,
      HelpfulOption.CHILD_LIFESTYLE,
      HelpfulOption.CHILD_HEALTH_NUTRITION_SAFETY,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    title: CategoryTitle.CHILD_HEALTH_CATEGORY,
    options: [
      HelpfulOption.CHILD_HEALTH_MEDICAL_CONDITION,
      HelpfulOption.SLEEP_HABITS_REGRESSION,
      HelpfulOption.BULLYING_SCHOOL_CONFLICT,
      HelpfulOption.SCREEN_TIME_INTERNET_SAFETY,
      HelpfulOption.PHYSICAL_EMOTIONAL_CHANGES_PUBERTY,
      HelpfulOption.NUTRITION_CHOOSY_EATERS
    ]
  },
  {
    title: CategoryTitle.CHILD_LIFESTYLE_CATEGORY,
    options: [
      HelpfulOption.CHILD_BEHAVIOR_CHALLENGES,
      HelpfulOption.PREPARING_CHILD_FOR_SIBLING,
      HelpfulOption.PHYSICAL_ACTIVITY_EXERCISE
    ]
  },
  {
    title: CategoryTitle.PARENT_SELF_CARE_CATEGORY,
    options: [
      HelpfulOption.PARENT_EMOTIONS_MENTAL_HEALTH,
      HelpfulOption.PARENTING_CHILD_SPECIAL_NEEDS,
      HelpfulOption.RELATIONSHIP_SUPPORT
    ]
  },
  {
    title: CategoryTitle.RETURN_TO_WORK_CATEGORY,
    options: [
      HelpfulOption.BALANCING_PARENTHOOD_CAREER,
      HelpfulOption.RETURNING_TO_WORK_WITH_OLDER_CHILDREN,
      HelpfulOption.EMOTIONS_AROUND_RETURNING_TO_WORK
    ]
  }
]

const parentingChildNineToTwelveYearsOptions: CategoryOption[] = [...parentingChildSixToEightYearsOptions]

// Menopause
const menopauseExploringOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.MENOPAUSE_SYMPTOMS,
      HelpfulOption.NUTRITION_WEIGHT_MANAGEMENT_SUPPORT,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.EARLY_MENOPAUSE_OR_PRIMARY_OVARIAN_INSUFFICIENCY,
      HelpfulOption.FERTILITY_PRESERVATION,
      HelpfulOption.ASSISTED_REPRODUCTION,
      HelpfulOption.TREATMENT_OPTIONS,
      HelpfulOption.NUTRITION_WEIGHT_MANAGEMENT_SUPPORT,
      HelpfulOption.COPING_SYMTOMS_AT_WORK
    ]
  }
]

const menopauseStartOfPeriMenopauseOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.MENOPAUSE_SYMPTOMS,
      HelpfulOption.TREATMENT_OPTIONS,
      HelpfulOption.NUTRITION_WEIGHT_MANAGEMENT_SUPPORT,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.EARLY_MENOPAUSE_OR_PRIMARY_OVARIAN_INSUFFICIENCY,
      HelpfulOption.FERTILITY_PRESERVATION,
      HelpfulOption.ASSISTED_REPRODUCTION,
      HelpfulOption.COPING_SYMTOMS_AT_WORK
    ]
  }
]

const menopauseSymptomsWithMenstrualCyclesOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.MENOPAUSE_SYMPTOMS,
      HelpfulOption.HORMONE_THERAPY_TREATMENT_OPTIONS,
      HelpfulOption.NUTRITION_WEIGHT_MANAGEMENT_SUPPORT,
      HelpfulOption.COPING_SYMTOMS_AT_WORK,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.EARLY_MENOPAUSE_OR_PRIMARY_OVARIAN_INSUFFICIENCY,
      HelpfulOption.FERTILITY_PRESERVATION,
      HelpfulOption.ASSISTED_REPRODUCTION,
      HelpfulOption.MEMORY_FOCUS_COGNITIVE_ISSUES,
      HelpfulOption.LOSS_OF_LIBIDO_SEXUAL_ISSUES,
      HelpfulOption.IMPROVING_SLEEP,
      HelpfulOption.URINARY_FREQUENCY_URGENCY,
      HelpfulOption.BONE_HEART_HEALTH,
      HelpfulOption.ORAL_HEALTH,
      HelpfulOption.LOW_TESTOSTERONE,
      HelpfulOption.WEIGHT_LOSS_MEDS_MENOPAUSE,
      HelpfulOption.SUPPLEMENTS_ALTERNATIVE_MEDS
    ]
  }
]

const menopauseNoMenstrualCyclesOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.MENOPAUSE_SYMPTOMS,
      HelpfulOption.HORMONE_THERAPY_TREATMENT_OPTIONS,
      HelpfulOption.NUTRITION_WEIGHT_MANAGEMENT_SUPPORT,
      HelpfulOption.COPING_SYMTOMS_AT_WORK,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.EARLY_MENOPAUSE_OR_PRIMARY_OVARIAN_INSUFFICIENCY,
      HelpfulOption.MEMORY_FOCUS_COGNITIVE_ISSUES,
      HelpfulOption.LOSS_OF_LIBIDO_SEXUAL_ISSUES,
      HelpfulOption.IMPROVING_SLEEP,
      HelpfulOption.URINARY_FREQUENCY_URGENCY,
      HelpfulOption.BONE_HEART_HEALTH,
      HelpfulOption.ORAL_HEALTH,
      HelpfulOption.LOW_TESTOSTERONE,
      HelpfulOption.WEIGHT_LOSS_MEDS_MENOPAUSE,
      HelpfulOption.SUPPLEMENTS_ALTERNATIVE_MEDS
    ]
  }
]

// Preservation
const preservationExploringOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.EGG_PRESERVATION,
      HelpfulOption.SPERM_PRESERVATION,
      HelpfulOption.CHAT_HEALTH,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.BASELINE_FERTILITY_TESTING,
      HelpfulOption.PRESERVATION_BEFORE_MEDICAL_PROCEDURE,
      HelpfulOption.GENETIC_SCREENING,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.PRE_EXISTING_MEDICAL_METABOLIC_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  }
]

// Adoption
const adoptionExploringOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.TALK_ADOPTION_EXPERT,
      HelpfulOption.FIND_ADOPTION_AGENCY,
      HelpfulOption.DOMESTIC_ADOPTION,
      HelpfulOption.INTERNATIONAL_ADOPTION,
      HelpfulOption.FOSTER_CARE_ADOPTION,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.ADOPTION_PROCESS_STEPS,
      HelpfulOption.ADOPTION_JOURNEY_LENGTH,
      HelpfulOption.ADOPTION_JOURNEY_COST,
      HelpfulOption.ADOPTION_PROVIDER_TYPES,
      HelpfulOption.NEWBORN_ADOPTION,
      HelpfulOption.ADOPTION_LEGAL_CONSIDERATIONS,
      HelpfulOption.LGBTQ_FAMILY,
      HelpfulOption.EMOTIONAL_SUPPORT
    ]
  }
]

const adoptionChoosingAgencyAttorneyOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.ADOPTION_BASICS,
      HelpfulOption.FIND_ADOPTION_AGENCY,
      HelpfulOption.DOMESTIC_ADOPTION,
      HelpfulOption.INTERNATIONAL_ADOPTION,
      HelpfulOption.FOSTER_CARE_ADOPTION,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.ADOPTION_PROCESS_STEPS,
      HelpfulOption.ADOPTION_JOURNEY_COST,
      HelpfulOption.ADOPTION_PROVIDER_TYPES,
      HelpfulOption.NEWBORN_ADOPTION,
      HelpfulOption.ADOPTION_LEGAL_CONSIDERATIONS,
      HelpfulOption.LGBTQ_FAMILY,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.ADOPTION_AGENCY_QUESTIONS,
      HelpfulOption.ADOPTION_ATTORNEY_QUESTIONS
    ]
  }
]

const adoptionWorkingWithAgencyAttorneyOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.ADOPTION_PROCESS_STEPS,
      HelpfulOption.ADOPTION_LEGAL_CONSIDERATIONS,
      HelpfulOption.DOMESTIC_ADOPTION,
      HelpfulOption.INTERNATIONAL_ADOPTION,
      HelpfulOption.FOSTER_CARE_ADOPTION,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.NEWBORN_ADOPTION,
      HelpfulOption.LGBTQ_FAMILY,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.ADOPTION_MATCHING_PROCESS,
      HelpfulOption.ADOPTIVE_PARENT_PROFILE
    ]
  }
]

const adoptionFinalStagesOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.ADOPTION_BIRTH_PLANNING,
      HelpfulOption.ADOPTION_LEGAL_FINALIZATION,
      HelpfulOption.POST_ADOPTION_EMOTIONS,
      HelpfulOption.PARENTING_AFTER_ADOPTION,
      HelpfulOption.NOT_SURE,
      HelpfulOption.FINANCE_INFO
    ]
  },
  {
    options: [
      HelpfulOption.ADOPTION_LEGAL_CONSIDERATIONS,
      HelpfulOption.LGBTQ_FAMILY,
      HelpfulOption.POST_PLACEMENT_SUPERVISION,
      HelpfulOption.CARING_FOR_NEWBORN,
      HelpfulOption.CARING_FOR_CHILD,
      HelpfulOption.PARENTING_CHILD_SPECIAL_NEEDS,
      HelpfulOption.TALKING_ABOUT_ADOPTION,
      HelpfulOption.ADOPTION_FUTURE_PLANNING
    ]
  }
]

// Exploring
const exploringNotSureParentOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.FERTILITY_BASICS,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.EXPLORING_DIFFERENT_PATHS_TO_PARENTHOOD,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.CONTRACEPTION_OPTIONS,
      HelpfulOption.FERTILITY_TESTING,
      HelpfulOption.FERTILITY_PRESERVATION,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.LGBTQ_FAMILY
    ]
  }
]

const exploringLearningFertilityOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.FERTILITY_BASICS,
      HelpfulOption.IMPROVING_FERTILITY_HEALTH,
      HelpfulOption.IMPROVING_METABOLIC_HEALTH,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.EXPLORING_DIFFERENT_PATHS_TO_PARENTHOOD,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.FERTILITY_TESTING,
      HelpfulOption.HORMONAL_CONTRACEPTION_AND_FERTILITY,
      HelpfulOption.OVULATION_TRACKING,
      HelpfulOption.IRREGULAR_CYCLES,
      HelpfulOption.FERTILITY_PRESERVATION,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.LGBTQ_FAMILY,
      HelpfulOption.WEIGHT_LOSS_MEDS_FERTILITY,
      HelpfulOption.PRE_EXISTING_MEDICAL_METABOLIC_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  }
]

const exploringNoPathOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.FERTILITY_BASICS,
      HelpfulOption.IMPROVING_FERTILITY_HEALTH,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.EXPLORING_DIFFERENT_PATHS_TO_PARENTHOOD,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.OVULATION_TRACKING,
      HelpfulOption.SPERM_HEALTH,
      HelpfulOption.PREPARING_FOR_PREGNANCY,
      HelpfulOption.WEIGHT_LOSS_MEDS_FERTILITY,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.FERTILITY_PRESERVATION,
      HelpfulOption.ASSISTED_REPRODUCTION,
      HelpfulOption.ADOPTION,
      HelpfulOption.GESTATIONAL_SURROGACY,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.LGBTQ_FAMILY,
      HelpfulOption.PRE_EXISTING_MEDICAL_METABOLIC_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  }
]

const exploringMultiplePathsOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PREPARING_FOR_PREGNANCY,
      HelpfulOption.ASSISTED_REPRODUCTION,
      HelpfulOption.ADOPTION,
      HelpfulOption.GESTATIONAL_SURROGACY,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.FERTILITY_TESTING,
      HelpfulOption.WEIGHT_LOSS_MEDS_FERTILITY,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.FERTILITY_PRESERVATION,
      HelpfulOption.PRE_EXISTING_MEDICAL_METABOLIC_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  }
]

const exploringNewOptionsOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PREGNANCY_LOSS,
      HelpfulOption.ASSISTED_REPRODUCTION,
      HelpfulOption.ADOPTION,
      HelpfulOption.GESTATIONAL_SURROGACY,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.FERTILITY_TESTING,
      HelpfulOption.FERTILITY_PRESERVATION,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.PRE_EXISTING_MEDICAL_METABOLIC_CONDITIONS,
      HelpfulOption.KNOWN_GENETIC_OR_HEREDITARY_CONDITIONS
    ]
  }
]

// Gestational Surrogacy
const gestationalSurrogacyExploringOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.GESTATIONAL_SURROGACY_STEPS,
      HelpfulOption.ASSISTED_REPRODUCTION_SUPPORT,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.GESTATIONAL_SURROGACY_LEGAL_CONSIDERATIONS,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [HelpfulOption.BENEFITS_WORKING_WITH_AGENCY, HelpfulOption.DONOR_ASSISTANCE, HelpfulOption.LGBTQ_FAMILY]
  }
]

const gestationalSurrogacyChoosingAgencyAttorneyOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.HOW_CHOOSE_GESTATIONAL_SURROGACY_AGENCY,
      HelpfulOption.GESTATIONAL_SURROGACY_WITHOUT_AGENCY,
      HelpfulOption.GESTATIONAL_SURROGACY_LEGAL_CONSIDERATIONS,
      HelpfulOption.ASSISTED_REPRODUCTION_SUPPORT,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.GESTATIONAL_CARRIER_MATCHING_PROCESS,
      HelpfulOption.CREATING_INTENDED_PARENT_PROFILE,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.LGBTQ_FAMILY
    ]
  }
]

const gestationalSurrogacyWorkingWithAgencyAttorneyOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.GESTATIONAL_SURROGACY_LEGAL_CONSIDERATIONS,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.GESTATIONAL_CARRIER_MATCHING_PROCESS,
      HelpfulOption.CREATING_INTENDED_PARENT_PROFILE,
      HelpfulOption.LGBTQ_FAMILY
    ]
  }
]

const gestationalSurrogacyOtherOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.GESTATIONAL_SURROGACY_LEGAL_CONSIDERATIONS,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.LGBTQ_FAMILY,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  }
]

const gestationalSurrogacyCarrierPregnantOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.BIRTH_PLANNING_PREP_INTENDED_PARENTS,
      HelpfulOption.FETAL_DEVELOPMENT_PRENATAL_CARE,
      HelpfulOption.DOULA_INFO,
      HelpfulOption.GESTATIONAL_SURROGACY_LEGAL_CONSIDERATIONS,
      HelpfulOption.RETURN_TO_WORK,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.SUPPORT_LACTATION_INFANT_FEEDING,
      HelpfulOption.POST_BIRTH_EMOTIONS_MENTAL_HEALTH,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [HelpfulOption.NEWBORN_CARE, HelpfulOption.LEARN_MILK_SHIPPING]
  },
  {
    title: CategoryTitle.FETAL_DEVELOPMENT_PRENATAL_CARE_CATEGORY,
    options: [
      HelpfulOption.FETAL_DEVELOPMENT_STAGES,
      HelpfulOption.GENETIC_SCREENING_PRENATAL_TESTING,
      HelpfulOption.ULTRASOUND_IMAGING,
      HelpfulOption.PREGNANCY_RISKS_COMPLICATIONS,
      HelpfulOption.HIGH_RISK_PREGNANCY_MULTIPLE_FETUSES,
      HelpfulOption.PREGNANCY_WARNING_SIGNS
    ]
  },
  {
    title: CategoryTitle.BIRTH_PLANNING_PREP_INTENDED_PARENTS_CATEGORY,
    options: [
      HelpfulOption.BIRTH_OPTIONS_SETTINGS,
      HelpfulOption.BIRTH_PLAN_RESOURCES,
      HelpfulOption.PREGNANCY_SUPPORT_TEAM,
      HelpfulOption.EMOTIONAL_PREPARATION,
      HelpfulOption.TRAVEL_CONSIDERATIONS,
      HelpfulOption.PARENTAL_LEAVE_PLAN,
      HelpfulOption.PRENATAL_EDUCATION_PARENTING_CLASSES
    ]
  },
  {
    title: CategoryTitle.POST_BIRTH_EMOTIONS_MENTAL_HEALTH_CATEGORY,
    options: [
      HelpfulOption.NEW_PARENT_EMOTIONS,
      HelpfulOption.PERINATAL_MOOD_DISORDERS,
      HelpfulOption.TRAUMATIC_BIRTH,
      HelpfulOption.BIRTH_IN_NICU,
      HelpfulOption.SELF_CARE,
      HelpfulOption.FINDING_HELP_SUPPORT
    ]
  },
  {
    title: CategoryTitle.RETURN_TO_WORK_CATEGORY,
    options: [
      HelpfulOption.RETURN_TO_WORK_PLAN,
      HelpfulOption.EMOTIONAL_CHALLENGES_WORK,
      HelpfulOption.MANAGER_CONVERSATIONS,
      HelpfulOption.WORK_LIFE_BALANCE,
      HelpfulOption.SLEEP_DEPRIVATION_WORK,
      HelpfulOption.REMOTE_WORK_NEWBORN
    ]
  },
  {
    title: CategoryTitle.INFANT_FEEDING_CATEGORY,
    options: [
      HelpfulOption.INDUCED_LACTATION,
      HelpfulOption.COMBINATION_FEEDING,
      HelpfulOption.NICU_FEEDING,
      HelpfulOption.RETURN_TO_WORK_FEEDING
    ]
  }
]

// Low Testosterone

const lowTestosteroneExploringOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.CHAT_HEALTH,
      HelpfulOption.LOW_TESTOSTERONE_SYMPTOMS,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.LOW_TESTOSTERONE_TREATMENT_OPTIONS,
      HelpfulOption.NUTRITION_WEIGHT_MANAGEMENT_SUPPORT,
      HelpfulOption.FERTILITY_PRESERVATION,
      HelpfulOption.ASSISTED_REPRODUCTION,
      HelpfulOption.LOW_TESTOSTERONE_PERI_MENOPAUSE
    ]
  }
]

const lowTestosteroneExperiencingSymptomsOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.LOW_TESTOSTERONE_SYMPTOMS,
      HelpfulOption.LOW_TESTOSTERONE_TREATMENT_OPTIONS,
      HelpfulOption.COPING_WITH_SYMPTOMS,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.CHAT_HEALTH,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.NUTRITION_WEIGHT_MANAGEMENT_SUPPORT,
      HelpfulOption.LOSS_OF_LIBIDO_SEXUAL_ISSUES,
      HelpfulOption.SLEEP_APNEA_SLEEP_ISSUES,
      HelpfulOption.CARDIOVASCULAR_HEALTH,
      HelpfulOption.FERTILITY_PRESERVATION,
      HelpfulOption.ASSISTED_REPRODUCTION,
      HelpfulOption.LOW_TESTOSTERONE_PERI_MENOPAUSE
    ]
  }
]

const lowTestosteroneDiagnosedOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.LOW_TESTOSTERONE_TREATMENT_OPTIONS,
      HelpfulOption.COPING_WITH_SYMPTOMS,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.CHAT_HEALTH,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.NUTRITION_WEIGHT_MANAGEMENT_SUPPORT,
      HelpfulOption.LOSS_OF_LIBIDO_SEXUAL_ISSUES,
      HelpfulOption.SLEEP_APNEA_SLEEP_ISSUES,
      HelpfulOption.CARDIOVASCULAR_HEALTH,
      HelpfulOption.FERTILITY_PRESERVATION,
      HelpfulOption.ASSISTED_REPRODUCTION,
      HelpfulOption.LOW_TESTOSTERONE_PERI_MENOPAUSE
    ]
  }
]

// GAC
const genderAffirmingCareExploringOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.MEDICAL_OR_HEALTH_RELATED_QUESTIONS,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [HelpfulOption.FERTILITY_PRESERVATION, HelpfulOption.GENDER_EXPLORATION_EMBODIMENT]
  }
]

const hormoneTherapyInterestOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.FEMINIZING_HORMONE_THERAPY,
      HelpfulOption.MASCULINIZING_HORMONE_THERAPY,
      HelpfulOption.MEDICAL_OR_HEALTH_RELATED_QUESTIONS,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.GENDER_EXPLORATION_EMBODIMENT,
      HelpfulOption.SOCIAL_AFFIRMATION,
      HelpfulOption.MEDICAL_AFFIRMATION,
      HelpfulOption.LEGAL_AFFIRMATION,
      HelpfulOption.FERTILITY_PRESERVATION
    ]
  }
]

const nonHormoneTherapyInterestOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.MEDICAL_OR_HEALTH_RELATED_QUESTIONS,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [
      HelpfulOption.GENDER_EXPLORATION_EMBODIMENT,
      HelpfulOption.BINDERS_AND_TUCKING_GARMENTS,
      HelpfulOption.VOICE_THERAPY,
      HelpfulOption.HAIR_REMOVAL,
      HelpfulOption.SOCIAL_AFFIRMATION,
      HelpfulOption.MEDICAL_AFFIRMATION,
      HelpfulOption.LEGAL_AFFIRMATION,
      HelpfulOption.FERTILITY_PRESERVATION
    ]
  }
]

const onHormonesOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.FEMINIZING_HORMONE_THERAPY,
      HelpfulOption.MASCULINIZING_HORMONE_THERAPY,
      HelpfulOption.INJECTION_GUIDANCE,
      HelpfulOption.MEDICAL_OR_HEALTH_RELATED_QUESTIONS,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [HelpfulOption.SOCIAL_AFFIRMATION, HelpfulOption.MEDICAL_AFFIRMATION, HelpfulOption.LEGAL_AFFIRMATION]
  }
]

const goingOffHormonesOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.NON_HORMONAL_GENDER_AFFIRMING_CARE_OPTIONS,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.MEDICAL_OR_HEALTH_RELATED_QUESTIONS,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  },
  {
    options: [HelpfulOption.FERTILITY_PRESERVATION, HelpfulOption.OTHER_FERTILITY_QUESTIONS]
  }
]

type JourneyStageCategoryOptionMap = Partial<Record<JourneyStage, CategoryOption[]>>
type CategorizedOptionsJourneyMap = Partial<Record<Journey, JourneyStageCategoryOptionMap>>

export const categorizedOptionsJourneyMap: CategorizedOptionsJourneyMap = {
  [Journey.PREGNANT]: {
    [JourneyStage.PREGNANT_FIRST]: pregnantFirstTrimesterOptions,
    [JourneyStage.PREGNANT_SECOND]: pregnantSecondTrimesterOptions,
    [JourneyStage.PREGNANT_THIRD]: pregnantThirdTrimesterOptions,
    [JourneyStage.POSTPARTUM]: pregnantPostpartumOptions
  },
  [Journey.TRY_PREGNANT]: {
    [JourneyStage.HAVE_NOT_STARTED]: haveNotStartedYetOptions,
    [JourneyStage.TRY_PREGNANT_LESS_THAN_SIX_MONTHS]: tryingLessThanSixMonths,
    [JourneyStage.TRY_PREGNANT_SIX_TO_TWELVE_MONTHS]: tryingSixToTwelveMonths,
    [JourneyStage.TRY_PREGNANT_OVER_ONE_YEAR]: tryingOverOneYear
  },
  [Journey.ASSISTED_REPRODUCTION]: {
    [JourneyStage.EXPLORING]: exploringArtOptions,
    [JourneyStage.MIGHT_WANT_IUI_IVF]: mightWantArtOptions,
    [JourneyStage.GETTING_READY_START_IUI]: gettingReadyStartIuiOptions,
    [JourneyStage.GETTING_READY_START_IVF]: gettingReadyStartIvfOptions,
    [JourneyStage.ALREADY_STARTED_IUI_CYCLE]: alreadyStartedIuiOptions,
    [JourneyStage.ALREADY_STARTED_IVF_CYCLE]: alreadyStartedIvfOptions,
    [JourneyStage.RECENTLY_COMPLETED_IUI_CYCLE]: recentlyCompletedIuiOptions,
    [JourneyStage.RECENTLY_COMPLETED_IVF_CYCLE]: recentlyCompletedIvfOptions
  },
  [Journey.PARENTING]: {
    [JourneyStage.CHILD_LESS_THAN_YEAR]: parentingChildLessThanYearOptions,
    [JourneyStage.CHILD_ONE_TO_TWO_YEARS]: parentingChildOneToTwoYearsOptions,
    [JourneyStage.CHILD_THREE_TO_FIVE_YEARS]: parentingChildThreeToFiveYearsOptions,
    [JourneyStage.CHILD_SIX_TO_EIGHT_YEARS]: parentingChildSixToEightYearsOptions,
    [JourneyStage.CHILD_NINE_TO_TWELVE_YEARS]: parentingChildNineToTwelveYearsOptions
  },
  [Journey.MENOPAUSE]: {
    [JourneyStage.EXPLORING]: menopauseExploringOptions,
    [JourneyStage.START_OF_PERI_MENOPAUSE]: menopauseStartOfPeriMenopauseOptions,
    [JourneyStage.MENOPAUSE_SYMPTOMS_WITH_MENSTRUAL_CYCLES]: menopauseSymptomsWithMenstrualCyclesOptions,
    [JourneyStage.NO_MENSTRUAL_CYCLES]: menopauseNoMenstrualCyclesOptions
  },
  [Journey.PRESERVATION]: {
    [JourneyStage.EXPLORING]: preservationExploringOptions
  },
  [Journey.ADOPTION]: {
    [JourneyStage.EXPLORING]: adoptionExploringOptions,
    [JourneyStage.CHOOSING_AGENCY_ATTORNEY]: adoptionChoosingAgencyAttorneyOptions,
    [JourneyStage.WORKING_WITH_AGENCY_ATTORNEY]: adoptionWorkingWithAgencyAttorneyOptions,
    [JourneyStage.FINAL_STAGES]: adoptionFinalStagesOptions
  },
  [Journey.EXPLORING]: {
    [JourneyStage.NOT_SURE_PARENT]: exploringNotSureParentOptions,
    [JourneyStage.LEARNING_FERTILITY]: exploringLearningFertilityOptions,
    [JourneyStage.EXPLORING_NO_PATH]: exploringNoPathOptions,
    [JourneyStage.EXPLORING_MULTIPLE_PATHS]: exploringMultiplePathsOptions,
    [JourneyStage.EXPLORING_NEW_OPTIONS]: exploringNewOptionsOptions
  },
  [Journey.GESTATIONAL]: {
    [JourneyStage.EXPLORING]: gestationalSurrogacyExploringOptions,
    [JourneyStage.CHOOSING_AGENCY_CLINIC_ATTORNEY]: gestationalSurrogacyChoosingAgencyAttorneyOptions,
    [JourneyStage.WORKING_WITH_AGENCY_CLINIC_ATTORNEY]: gestationalSurrogacyWorkingWithAgencyAttorneyOptions,
    [JourneyStage.GESTATIONAL_PREGNANT]: gestationalSurrogacyCarrierPregnantOptions,
    [JourneyStage.OTHER]: gestationalSurrogacyOtherOptions
  },
  [Journey.LOW_TESTOSTERONE]: {
    [JourneyStage.EXPLORING]: lowTestosteroneExploringOptions,
    [JourneyStage.EXPERIENCING_LOW_T_SYMPTOMS]: lowTestosteroneExperiencingSymptomsOptions,
    [JourneyStage.DIAGNOSED_LOW_T]: lowTestosteroneDiagnosedOptions
  },
  [Journey.GENDER_AFFIRMING_CARE]: {
    [JourneyStage.EXPLORING]: genderAffirmingCareExploringOptions,
    [JourneyStage.HORMONE_THERAPY_INTEREST]: hormoneTherapyInterestOptions,
    [JourneyStage.NON_HORMONAL_SUPPORT_INTEREST]: nonHormoneTherapyInterestOptions,
    [JourneyStage.ON_HORMONES]: onHormonesOptions,
    [JourneyStage.GOING_OFF_HORMONES]: goingOffHormonesOptions
  }
}

const preservationReadySpermOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PRESERVATION_PROCESS,
      HelpfulOption.PRESERVATION_BEFORE_MEDICAL_PROCEDURE,
      HelpfulOption.OPTIMIZING_SPERM_HEALTH,
      HelpfulOption.PREPARING_FOR_SPERM_PRESERVATION,
      HelpfulOption.BASELINE_FERTILITY_TESTING,
      HelpfulOption.GENETIC_SCREENING,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.NOT_SURE
    ]
  }
]

const preservationReadyEggsOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PRESERVATION_PROCESS,
      HelpfulOption.PRESERVATION_BEFORE_MEDICAL_PROCEDURE,
      HelpfulOption.OPTIMIZING_EGG_HEALTH,
      HelpfulOption.PREPARING_FOR_EGG_PRESERVATION,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.BASELINE_FERTILITY_TESTING,
      HelpfulOption.GENETIC_SCREENING,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.NOT_SURE
    ]
  }
]

const preservationReadyEmbryosOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PRESERVATION_PROCESS,
      HelpfulOption.CHAT_HEALTH,
      HelpfulOption.GENETIC_SCREENING,
      HelpfulOption.PREIMPLANTATION_GENETIC_TESTING,
      HelpfulOption.FERTILIZATION_METHODS,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.NOT_SURE
    ]
  }
]

const preservationReadyEggsEmbryosOrSpermOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PRESERVATION_PROCESS,
      HelpfulOption.CHAT_HEALTH,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.OPTIMIZING_EGG_HEALTH,
      HelpfulOption.OPTIMIZING_SPERM_HEALTH,
      HelpfulOption.GENETIC_SCREENING,
      HelpfulOption.PREIMPLANTATION_GENETIC_TESTING,
      HelpfulOption.FERTILIZATION_METHODS,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.HELP_FINDING_PROVIDER,
      HelpfulOption.NOT_SURE
    ]
  }
]

const preservationStartedSpermOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PREPARING_FOR_SPERM_RETRIEVAL,
      HelpfulOption.OPTIONS_FOR_SEMEN_COLLECTION,
      HelpfulOption.CRYOBANK_CRYOSHIPPING_INFO,
      HelpfulOption.CHAT_HEALTH,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  }
]

const preservationStartedEggsOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.CHAT_HEALTH,
      HelpfulOption.INJECTION_GUIDANCE,
      HelpfulOption.PREPARING_FOR_EGG_RETRIEVAL,
      HelpfulOption.CRYOBANK_CRYOSHIPPING_INFO,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  }
]

const preservationStartedEmbryosOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PRESERVATION_PROCESS,
      HelpfulOption.CHAT_HEALTH,
      HelpfulOption.GENETIC_SCREENING,
      HelpfulOption.PREIMPLANTATION_GENETIC_TESTING,
      HelpfulOption.FERTILIZATION_METHODS,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  }
]

const preservationStartedEggsEmbryosOrSpermOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.PRESERVATION_PROCESS,
      HelpfulOption.CHAT_HEALTH,
      HelpfulOption.ADVANCED_MATERNAL_AGE_35_PLUS,
      HelpfulOption.PREPARING_FOR_EGG_RETRIEVAL,
      HelpfulOption.PREPARING_FOR_SPERM_RETRIEVAL,
      HelpfulOption.OPTIONS_FOR_SEMEN_COLLECTION,
      HelpfulOption.CRYOBANK_CRYOSHIPPING_INFO,
      HelpfulOption.GENETIC_SCREENING,
      HelpfulOption.PREIMPLANTATION_GENETIC_TESTING,
      HelpfulOption.FERTILIZATION_METHODS,
      HelpfulOption.DONOR_ASSISTANCE,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  }
]

const preservationRecentlyReceivedSpermOptions: CategoryOption[] = [
  {
    title: CategoryTitle.INITIAL_CATEGORY,
    options: [
      HelpfulOption.CHAT_HEALTH,
      HelpfulOption.CRYOBANK_CRYOSHIPPING_INFO,
      HelpfulOption.EMOTIONAL_SUPPORT,
      HelpfulOption.FINANCE_INFO,
      HelpfulOption.NOT_SURE
    ]
  }
]

const preservationRecentlyReceivedEggsOptions: CategoryOption[] = [...preservationRecentlyReceivedSpermOptions]

const preservationRecentlyReceivedEmbryosOptions: CategoryOption[] = [...preservationRecentlyReceivedSpermOptions]

const preservationRecentlyReceivedEggsEmbryosOrSpermOptions: CategoryOption[] = [
  ...preservationRecentlyReceivedSpermOptions
]

type PreservationOptionsMap = Partial<Record<JourneyStage, Partial<Record<PreservationInterest, CategoryOption[]>>>>

export const categorizedOptionsPreservationInterestMap: PreservationOptionsMap = {
  [JourneyStage.PRESERVATION_READY]: {
    [PreservationInterest.PRESERVE_SPERM]: preservationReadySpermOptions,
    [PreservationInterest.PRESERVE_EGGS]: preservationReadyEggsOptions,
    [PreservationInterest.PRESERVE_EMBRYOS]: preservationReadyEmbryosOptions,
    [PreservationInterest.PRESERVE_EGGS_EMBRYOS_OR_SPERM]: preservationReadyEggsEmbryosOrSpermOptions
  },
  [JourneyStage.PRESERVATION_STARTED]: {
    [PreservationInterest.PRESERVE_SPERM]: preservationStartedSpermOptions,
    [PreservationInterest.PRESERVE_EGGS]: preservationStartedEggsOptions,
    [PreservationInterest.PRESERVE_EMBRYOS]: preservationStartedEmbryosOptions,
    [PreservationInterest.PRESERVE_EGGS_EMBRYOS_OR_SPERM]: preservationStartedEggsEmbryosOrSpermOptions
  },
  [JourneyStage.PRESERVATION_RECENTLY_RECEIVED]: {
    [PreservationInterest.PRESERVE_SPERM]: preservationRecentlyReceivedSpermOptions,
    [PreservationInterest.PRESERVE_EGGS]: preservationRecentlyReceivedEggsOptions,
    [PreservationInterest.PRESERVE_EMBRYOS]: preservationRecentlyReceivedEmbryosOptions,
    [PreservationInterest.PRESERVE_EGGS_EMBRYOS_OR_SPERM]: preservationRecentlyReceivedEggsEmbryosOrSpermOptions
  }
}
